// Acervo CAC (armas e documentos CR/CRAF/GT/porte) — ver docs/API.md do planbackgo, "Acervo CAC".
// Todas as rotas exigem `features.cac` (liberação do admin) além da permissão `acervo.*`.
import type { Document } from './document';

export type FirearmKind = 'pistola' | 'revolver' | 'carabina' | 'espingarda' | 'fuzil' | 'outro';
export type FirearmStatus = 'active' | 'sold' | 'transferred';

export interface FirearmInput {
  kind: FirearmKind;
  brand: string;
  model: string;
  caliber: string;
  serial_number: string;
  sigma_number?: string | null;
  acquisition_date?: string | null;
  /** Centavos (BIGINT no banco). */
  acquisition_value_cents?: number | null;
  status: FirearmStatus;
  notes?: string | null;
}

export interface Firearm extends FirearmInput {
  id: number;
  documents_count: number;
  /** Na lista: só os vigentes; na ficha (GET /firearms/:id): todos, inclusive renovados. */
  cac_documents?: CacDocument[];
  /** Só na ficha: arquivos com `ref_type = firearm`. */
  documents?: Document[];
  created_at: string;
  updated_at?: string;
}

export type CacDocType = 'cr' | 'craf' | 'gt' | 'porte' | 'outro';

/** Derivado: `renewed` se já renovado; `expired`; `expiring` (≤ 90 dias); senão `valid`. */
export type CacDocStatus = 'valid' | 'expiring' | 'expired' | 'renewed';

export interface CacDocumentInput {
  doc_type: CacDocType;
  /** Obrigatório para craf/gt; ausente para cr/porte/outro. */
  firearm_id?: number | null;
  number?: string | null;
  issuer_unit?: string | null;
  issued_at?: string | null;
  expires_at: string;
  /** Arquivo (Document) vinculado. */
  document_id?: number | null;
  notes?: string | null;
}

export interface CacDocument extends CacDocumentInput {
  id: number;
  /** "Marca Modelo · calibre" quando há arma. */
  firearm_label?: string;
  status: CacDocStatus;
  days_to_expire: number;
  renewed_by_cac_document_id?: number;
  document?: Document;
  renewed_by?: CacDocument;
  created_at: string;
  updated_at?: string;
}

export type CacDocStatusFilter = CacDocStatus | 'all' | '';

export interface CacDocumentFilter {
  doc_type?: CacDocType | '';
  firearm_id?: number;
  status?: CacDocStatusFilter;
}

/** Item consolidado de vencimento (documento CAC ou documento comum com validade). */
export interface ExpiringItem {
  kind: 'cac_document' | 'document';
  id: number;
  title: string;
  doc_type?: CacDocType;
  firearm_label?: string;
  expires_at: string;
  days_to_expire: number;
  status: 'expiring' | 'expired';
}

// ---------------------------------------------------------------- rótulos

export const FIREARM_KINDS: FirearmKind[] = ['pistola', 'revolver', 'carabina', 'espingarda', 'fuzil', 'outro'];

export const FIREARM_KIND_LABELS: Record<FirearmKind, string> = {
  pistola: 'Pistola',
  revolver: 'Revólver',
  carabina: 'Carabina',
  espingarda: 'Espingarda',
  fuzil: 'Fuzil',
  outro: 'Outro',
};

export const FIREARM_STATUSES: FirearmStatus[] = ['active', 'sold', 'transferred'];

export const FIREARM_STATUS_LABELS: Record<FirearmStatus, string> = {
  active: 'Ativa',
  sold: 'Vendida',
  transferred: 'Transferida',
};

export const CAC_DOC_TYPES: CacDocType[] = ['cr', 'craf', 'gt', 'porte', 'outro'];

export const CAC_DOC_TYPE_LABELS: Record<CacDocType, string> = {
  cr: 'CR',
  craf: 'CRAF',
  gt: 'Guia de tráfego',
  porte: 'Porte',
  outro: 'Outro',
};

/** Tipos que pertencem a uma arma específica (exigem `firearm_id`). */
export const CAC_DOC_TYPES_WITH_FIREARM: CacDocType[] = ['craf', 'gt'];

export const requiresFirearm = (docType: CacDocType): boolean => CAC_DOC_TYPES_WITH_FIREARM.includes(docType);

export const CAC_DOC_STATUS_LABELS: Record<CacDocStatus, string> = {
  valid: 'Válido',
  expiring: 'Vencendo',
  expired: 'Vencido',
  renewed: 'Renovado',
};

/** "Marca Modelo · calibre" montado no front (mesmo formato do `firearm_label` do backend). */
export const firearmLabel = (f: Pick<Firearm, 'brand' | 'model' | 'caliber'>): string =>
  `${f.brand} ${f.model} · ${f.caliber}`.trim();
